<template>
  <div class="onboarding-review">
    <h2 class="text-h5 font-weight-bold mb-2">
      {{ $t('onboarding.wizard.review.title') }}
    </h2>
    <p class="text-body-2 text-medium-emphasis mb-6">
      {{ $t('onboarding.wizard.review.subtitle') }}
    </p>

    <v-card
      rounded="xl"
      elevation="0"
      :style="{ border: '1px solid rgba(var(--v-border-color), 0.12)' }"
      class="pa-6 mb-4"
    >
      <div class="d-flex align-center mb-4">
        <h3 class="text-subtitle-1 font-weight-bold">
          {{ $t('onboarding.wizard.data-entry.company-info') }}
        </h3>
      </div>

      <ReviewRow
        editable
        :label="$t('onboarding.wizard.basic-info.company-name')"
        :value="formData.name"
        @update:value="emitField('name', $event)"
      />
      <ReviewRow
        editable
        :label="$t('onboarding.wizard.basic-info.website-url')"
        :value="formData.website"
        @update:value="emitField('website', $event)"
      />
      <ReviewRow
        editable
        :label="$t('onboarding.wizard.data-entry.street')"
        :value="formData.street"
        @update:value="emitField('street', $event)"
      />
      <ReviewRow
        editable
        :label="$t('onboarding.wizard.data-entry.city')"
        :value="formData.city"
        @update:value="emitField('city', $event)"
      />
      <ReviewRow
        editable
        :label="$t('onboarding.wizard.data-entry.zipcode')"
        :value="formData.zipcode"
        @update:value="emitField('zipcode', $event)"
      />
      <ReviewRow
        editable
        type="select"
        :items="countryItems"
        :label="$t('onboarding.wizard.data-entry.country')"
        :value="countryCode"
        @update:value="setCountry($event)"
      />
      <ReviewRow
        v-if="isUsOnboarding"
        editable
        :label="$t('onboarding.wizard.data-entry.state')"
        :value="formData.state"
        @update:value="emitField('state', $event)"
      />
      <ReviewRow
        v-if="isUsOnboarding"
        editable
        :label="$t('onboarding.wizard.data-entry.brokerage-name')"
        :value="formData.brokerageName"
        @update:value="emitField('brokerageName', $event)"
      />
      <ReviewRow
        v-if="isUsOnboarding"
        editable
        :label="$t('onboarding.wizard.data-entry.license-number')"
        :value="formData.licenseNumber"
        @update:value="emitField('licenseNumber', $event)"
      />
      <ReviewRow
        v-if="isCommissionEditable"
        editable
        :label="$t('onboarding.wizard.data-entry.commission-rate')"
        :value="formData.commission"
        :rules="[commissionRule]"
        @update:value="emitField('commission', $event)"
      />
      <ReviewRow
        editable
        :label="$t('onboarding.wizard.data-entry.slogan')"
        :value="formData.slogan"
        @update:value="emitField('slogan', $event)"
      />
    </v-card>

    <v-card
      rounded="xl"
      elevation="0"
      :style="{ border: '1px solid rgba(var(--v-border-color), 0.12)' }"
      class="pa-6 mb-4"
    >
      <div class="d-flex align-center mb-4">
        <h3 class="text-subtitle-1 font-weight-bold">
          {{ $t('onboarding.wizard.data-entry.responsible-person') }}
        </h3>
      </div>

      <ReviewRow
        editable
        :label="$t('onboarding.wizard.data-entry.contact-first-name')"
        :value="formData.contactFirstName"
        @update:value="emitField('contactFirstName', $event)"
      />
      <ReviewRow
        editable
        :label="$t('onboarding.wizard.data-entry.contact-last-name')"
        :value="formData.contactLastName"
        @update:value="emitField('contactLastName', $event)"
      />
      <ReviewRow
        editable
        :label="$t('onboarding.wizard.data-entry.phone')"
        :value="formData.contactPhone"
        @update:value="emitField('contactPhone', $event)"
      />
      <ReviewRow
        editable
        :label="$t('onboarding.submit.fields.email')"
        :value="formData.contactEmail"
        :rules="[emailRule]"
        @update:value="emitField('contactEmail', $event)"
      />
    </v-card>

    <v-card
      rounded="xl"
      elevation="0"
      :style="{ border: '1px solid rgba(var(--v-border-color), 0.12)' }"
      class="pa-6 mb-4"
    >
      <div class="d-flex align-center mb-4">
        <h3 class="text-subtitle-1 font-weight-bold">
          {{ $t('onboarding.wizard.data-entry.public-facing-ambassador') }}
        </h3>
      </div>

      <ReviewRow
        editable
        type="select"
        :items="salutationItems"
        :label="$t('onboarding.wizard.data-entry.salutation')"
        :value="formData.ambassadorSalutation"
        @update:value="emitField('ambassadorSalutation', $event)"
      />
      <ReviewRow
        editable
        :label="$t('onboarding.wizard.data-entry.contact-first-name')"
        :value="formData.ambassadorFirstName"
        @update:value="emitField('ambassadorFirstName', $event)"
      />
      <ReviewRow
        editable
        :label="$t('onboarding.wizard.data-entry.contact-last-name')"
        :value="formData.ambassadorLastName"
        @update:value="emitField('ambassadorLastName', $event)"
      />
      <ReviewRow
        editable
        :label="$t('onboarding.wizard.data-entry.phone')"
        :value="formData.phone"
        @update:value="emitField('phone', $event)"
      />
      <ReviewRow
        editable
        :label="$t('onboarding.wizard.data-entry.mobile')"
        :value="formData.ambassadorMobile"
        @update:value="emitField('ambassadorMobile', $event)"
      />
      <ReviewRow
        editable
        :label="$t('onboarding.submit.fields.role')"
        :value="formData.ambassadorRole"
        @update:value="emitField('ambassadorRole', $event)"
      />
      <ReviewRow
        editable
        :label="$t('onboarding.submit.fields.email')"
        :value="formData.ambassadorEmail"
        :rules="[emailRule]"
        @update:value="emitField('ambassadorEmail', $event)"
      />
    </v-card>

    <v-card
      rounded="xl"
      elevation="0"
      :style="{ border: '1px solid rgba(var(--v-border-color), 0.12)' }"
      class="pa-6 mb-4"
    >
      <div class="d-flex align-center mb-4">
        <h3 class="text-subtitle-1 font-weight-bold">
          {{ $t('onboarding.wizard.data-entry.company-images') }}
        </h3>
      </div>

      <div class="review-images">
        <div
          v-for="image in reviewImages"
          :key="image.key"
          class="review-image-slot"
        >
          <div
            class="review-image-frame"
            :class="{ 'review-image-frame--empty': !image.url }"
          >
            <r-img
              v-if="image.url"
              :src="image.url"
              cover
              class="review-image-frame__img"
            />
            <v-icon
              v-else
              size="28"
              color="grey"
            >
              mdi-image-outline
            </v-icon>
          </div>
          <span class="text-body-2 text-medium-emphasis">{{ image.label }}</span>
        </div>
      </div>
    </v-card>

    <v-card
      rounded="xl"
      elevation="0"
      :style="{ border: '1px solid rgba(var(--v-border-color), 0.12)' }"
      class="pa-6 mb-4"
    >
      <div class="d-flex align-center mb-4">
        <h3 class="text-subtitle-1 font-weight-bold">
          {{ $t('onboarding.wizard.data-entry.brand-colors') }}
        </h3>
      </div>

      <v-row>
        <v-col
          v-for="color in brandColors"
          :key="color.field"
          cols="12"
          md="6"
        >
          <span class="text-body-2 font-weight-medium d-block mb-1">{{ color.label }}</span>
          <div class="d-flex align-center ga-2">
            <input
              type="color"
              class="review-color-input"
              :value="colorInputValue(color.value)"
              :aria-label="color.label"
              @input="emitField(color.field, ($event.target as HTMLInputElement).value)"
            />
            <v-text-field
              :model-value="color.value"
              class="flex-grow-1"
              variant="outlined"
              density="compact"
              rounded="lg"
              hide-details
              placeholder="#000000"
              @update:model-value="emitField(color.field, $event ?? '')"
            />
          </div>
        </v-col>
      </v-row>
    </v-card>

    <v-card
      rounded="xl"
      elevation="0"
      :style="{ border: '1px solid rgba(var(--v-border-color), 0.12)' }"
      class="pa-6 mb-6"
    >
      <div class="d-flex align-center mb-2">
        <h3 class="text-subtitle-1 font-weight-bold">
          {{ $t('onboarding.wizard.review.legal-urls-title') }}
        </h3>
      </div>
      <p class="text-body-2 text-medium-emphasis mb-4">
        {{ $t('onboarding.wizard.review.legal-urls-hint') }}
      </p>

      <v-text-field
        class="mb-3"
        :model-value="formData.linkImprint"
        variant="outlined"
        density="comfortable"
        rounded="lg"
        :label="$t('onboarding.company-data.web.imprint')"
        :placeholder="$t('onboarding.company-data.web.imprintPlaceholder')"
        :rules="[optionalHttpUrlRule]"
        hide-details="auto"
        @update:model-value="emitField('linkImprint', $event)"
      />
      <v-text-field
        class="mb-3"
        :model-value="formData.linkPrivacy"
        variant="outlined"
        density="comfortable"
        rounded="lg"
        :label="$t('onboarding.company-data.web.privacy')"
        :placeholder="$t('onboarding.company-data.web.privacyPlaceholder')"
        :rules="[optionalHttpUrlRule]"
        hide-details="auto"
        @update:model-value="emitField('linkPrivacy', $event)"
      />
      <v-text-field
        class="mb-4"
        :model-value="formData.linkAbout"
        variant="outlined"
        density="comfortable"
        rounded="lg"
        :label="$t('onboarding.company-data.web.about')"
        :placeholder="$t('onboarding.company-data.web.aboutPlaceholder')"
        :rules="[optionalHttpUrlRule]"
        hide-details="auto"
        @update:model-value="emitField('linkAbout', $event)"
      />
    </v-card>

    <slot name="submit-actions" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  availableCountryCode,
  availableCountryRows,
  resolveAvailableCountry,
} from '@/lib/onboardingCountry'
import { email as emailRule } from '@/lib/validation'

import { useOnboardingFeatures } from './composables/useOnboardingFeatures'
import type { OnboardingFormData } from './lib/onboardingPrefill'
import type { OnboardingWizardCompany } from './lib/onboardingWizardCompany'
import ReviewRow from './ReviewRow.vue'

const props = defineProps<{
  formData: OnboardingFormData
  company?: OnboardingWizardCompany | null
  /**
   * Shared, session-aware US-market flag from `useOnboardingForm` (index.vue). Recomputing it here
   * would drop the Genaisis `session.market` signal and hide US fields that validation still requires.
   */
  isUsOnboarding: boolean
}>()

const emit = defineEmits<{
  'update:field': [field: string, value: string]
}>()

const { t } = useI18n()

// Commission is a product-gated field. Show it whenever the product allows it —
// including when empty — so the user can add a value from the review.
const { isCommissionEditable } = useOnboardingFeatures()

/**
 * Company images shown back for confirmation (logo / team / ambassador portrait).
 * Falls back to the formData URL when the DB has no persisted image, so an AI candidate
 * pick — which lands in formData via the chat field-update path but is never written to
 * Company.images — previews here just like it does on the data-entry form, instead of
 * showing an empty slot. Mirrors the fallback chain in useOnboardingFormImages.
 */
const reviewImages = computed(() => [
  {
    key: 'logo',
    label: t('settings.company.images.labels.company-logo'),
    url: props.company?.images?.logo?.url || props.formData.companyLogo || '',
  },
  {
    key: 'team',
    label: t('settings.company.images.labels.team-picture'),
    url: props.company?.images?.team?.url || props.formData.teamPicture || '',
  },
  {
    key: 'portrait',
    label: t('settings.company.images.labels.portrait-picture'),
    url: props.company?.ambassador?.image?.url || props.formData.portraitImage || '',
  },
])

const salutationItems = computed(() => [
  { title: t('onboarding.wizard.data-entry.salutation-mr'), value: 'Herr' },
  { title: t('onboarding.wizard.data-entry.salutation-ms'), value: 'Frau' },
])

/** Same source/mapping as the data-entry country select — value is the ISO alpha-2 code. */
const countryItems = computed(() =>
  [...availableCountryRows()]
    .sort((a, b) => t(a.text).localeCompare(t(b.text)))
    .map((c) => ({ title: t(c.text), value: String(c.countryCode).toLowerCase() }))
)

/** `formData.country` stays a localized label; the select works in ISO codes. */
const countryCode = computed(() => availableCountryCode(props.formData.country, t) ?? '')

const setCountry = (code: string) => {
  const row = resolveAvailableCountry(code, t)
  emitField('country', row ? t(row.text) : '')
}

const brandColors = computed(() => [
  {
    field: 'primaryColor',
    label: t('onboarding.wizard.data-entry.primary-color'),
    value: props.formData.primaryColor,
  },
  {
    field: 'secondaryColor',
    label: t('onboarding.wizard.data-entry.secondary-color'),
    value: props.formData.secondaryColor,
  },
])

/** Native color input needs a valid #rrggbb; fall back to black when unset/invalid. */
const colorInputValue = (value: string): string =>
  /^#[0-9a-f]{6}$/i.test((value || '').trim()) ? value.trim() : '#000000'

const emitField = (field: string, value: string) => {
  emit('update:field', field, value ?? '')
}

/** Empty is allowed (commission is optional); non-empty must be a number in 0–100 (schema contract). */
const commissionRule = (v: string | null | undefined): true | string => {
  // Strip at most one optional trailing `%` (with optional space). Values like "1%2"
  // must stay invalid instead of collapsing to 12.
  const s = (v ?? '').toString().trim().replace(/\s*%$/, '').replace(',', '.').trim()
  if (!s) return true
  const n = Number(s)
  return Number.isFinite(n) && n >= 0 && n <= 100
    ? true
    : t('onboarding.wizard.review.invalid-commission')
}

/** Empty is allowed (submit falls back to website); non-empty must parse as HTTP(S) URL. */
const optionalHttpUrlRule = (v: string | null | undefined): true | string => {
  const s = (v ?? '').trim()
  if (!s) return true
  const withProto = /^https?:\/\//i.test(s) ? s : `https://${s}`
  return URL.canParse(withProto) ? true : t('onboarding.wizard.review.invalid-url')
}
</script>

<style scoped>
.review-images {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.review-image-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.review-image-frame {
  width: 96px;
  height: 96px;
  border-radius: 12px;
  border: 1px solid rgba(var(--v-border-color), 0.12);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(var(--v-border-color), 0.04);
}

.review-image-frame--empty {
  border-style: dashed;
}

.review-image-frame__img {
  width: 100%;
  height: 100%;
}

.review-color-input {
  width: 44px;
  height: 40px;
  padding: 0;
  border: 1px solid rgba(var(--v-border-color), 0.3);
  border-radius: 8px;
  background: none;
  cursor: pointer;
  flex-shrink: 0;
}
</style>
